import { useCallback, useEffect, useRef, useState } from 'react'

import { isIframeHeightMessage } from 'services/IframeMessaging'

import { DEFAULT_IFRAME_HEIGHT, IFRAME_MAX_HEIGHT, IFRAME_MIN_HEIGHT } from '../constants'

interface IUseIframeAutoHeightOptions {
  defaultHeight?: number
  minHeight?: number
  maxHeight?: number
  debounceMs?: number
}

export const useIframeAutoHeight = (options: IUseIframeAutoHeightOptions = {}) => {
  const {
    defaultHeight = DEFAULT_IFRAME_HEIGHT,
    minHeight = IFRAME_MIN_HEIGHT,
    maxHeight = IFRAME_MAX_HEIGHT,
    debounceMs = 0,
  } = options

  const [height, setHeight] = useState<number | string>(defaultHeight)
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const iframeIdRef = useRef<string>(`iframe-${Date.now()}-${Math.random()}`)
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null)
  const resizeObserverRef = useRef<ResizeObserver | null>(null)

  const updateHeight = useCallback(
    (newHeight: number) => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }

      debounceTimerRef.current = setTimeout(() => {
        const clampedHeight = Math.max(minHeight, Math.min(maxHeight, newHeight))
        setHeight(clampedHeight)
      }, debounceMs)
    },
    [minHeight, maxHeight, debounceMs],
  )

  const observeSameOriginContent = useCallback(() => {
    if (!iframeRef.current) {
      return
    }

    try {
      const iframeDoc = iframeRef.current.contentDocument
      const wrapper = iframeDoc?.getElementById('wrapper')
      const body = iframeDoc?.body
      if (!wrapper || !body) {
        return
      }

      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect()
      }

      resizeObserverRef.current = new ResizeObserver(() => {
        const marginTop = parseFloat(getComputedStyle(body).marginTop) || 0
        const marginBottom = parseFloat(getComputedStyle(body).marginBottom) || 0
        const rect = wrapper.getBoundingClientRect()
        updateHeight(rect.height + marginTop + marginBottom)
      })

      resizeObserverRef.current.observe(wrapper)
    } catch (e) {
      // Cross-origin iframe — fallback to postMessage
      // eslint-disable-next-line no-console
      console.debug('Cross-origin iframe detected, using postMessage for height')
    }
  }, [updateHeight])

  const handleMessage = useCallback(
    (event: MessageEvent) => {
      if (!isIframeHeightMessage(event.data)) {
        return
      }

      const { height: newHeight, iframeId } = event.data

      if (iframeId && iframeId === iframeIdRef.current) {
        updateHeight(newHeight)
      } else if (!iframeId && iframeRef.current?.contentWindow === event.source) {
        updateHeight(newHeight)
      }
    },
    [updateHeight],
  )

  useEffect(() => {
    window.addEventListener('message', handleMessage)
    return () => {
      window.removeEventListener('message', handleMessage)
    }
  }, [handleMessage])

  useEffect(() => {
    if (!iframeRef.current) {
      return
    }

    const handleLoad = () => {
      observeSameOriginContent()
    }

    const iframe = iframeRef.current
    iframe.addEventListener('load', handleLoad)

    return () => {
      iframe.removeEventListener('load', handleLoad)

      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect()
      }

      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }
    }
  }, [observeSameOriginContent])

  return {
    iframeRef,
    iframeId: iframeIdRef.current,
    height,
    checkHeight: observeSameOriginContent,
  }
}
